import { Newspaper, Projector, Users, BookOpen, CheckSquare, Clock, UsersRound, FolderKanban, CheckCircle, Calendar } from "lucide-react";
import { useLocation } from "wouter";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";
import { SectionHelp } from "@/components/help";

export default function QuickAccess() {
  const [, navigate] = useLocation();
  const { hasPermission } = useAuth();

  const quickAccessItems = [
    {
      icon: <Projector className="h-6 w-6" />,
      label: "Projects",
      path: "/projects",
      description: "View all projects"
    },
    {
      icon: <CheckSquare className="h-6 w-6" />,
      label: "My Tasks",
      path: "/my-tasks",
      description: "Manage your tasks"
    },
    {
      icon: <Clock className="h-6 w-6" />,
      label: "Timesheet",
      path: "/timesheet",
      description: "Log your hours"
    },
    {
      icon: <Newspaper className="h-6 w-6" />,
      label: "News",
      path: "/news",
      description: "Latest updates"
    },
    {
      icon: <Users className="h-6 w-6" />,
      label: "Directory",
      path: "/directory",
      description: "Team contacts"
    },
    {
      icon: <BookOpen className="h-6 w-6" />,
      label: "Knowledge",
      path: "/knowledge",
      description: "Documentation"
    },
  ];

  // Permission-based items
  if (hasPermission('resources.view_team') || hasPermission('resources.view_org')) {
    quickAccessItems.push({
      icon: <UsersRound className="h-6 w-6" />,
      label: "Resources",
      path: "/resources",
      description: "Team allocation"
    });
  }

  if (hasPermission('effort.view_team') || hasPermission('analytics.view_team')) {
    quickAccessItems.push({
      icon: <FolderKanban className="h-6 w-6" />,
      label: "Reports",
      path: "/effort-report",
      description: "Analytics & reports"
    });
  }

  if (hasPermission('leave.approve') && quickAccessItems.length < 8) {
    quickAccessItems.push({
      icon: <CheckCircle className="h-6 w-6" />,
      label: "Approvals",
      path: "/leave-approvals",
      description: "Pending leave requests"
    });
  }

  if (hasPermission('content.manage_events') && quickAccessItems.length < 8) {
    quickAccessItems.push({
      icon: <Calendar className="h-6 w-6" />,
      label: "Events",
      path: "/events",
      description: "Manage events"
    });
  }

  return (
    <div className="mb-8">
      <SectionHelp contentId="dashboard:quick-access" tooltip="Learn about quick access" className="mb-4">
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Quick Access</h2>
      </SectionHelp>
      <div className="grid grid-cols-4 sm:grid-cols-4 lg:grid-cols-8 gap-3">
        {quickAccessItems.slice(0, 8).map((item, index) => {
          return (
            <button
              key={index}
              onClick={() => navigate(item.path)}
              className={cn(
                "group relative flex flex-col items-center justify-center p-4 rounded-xl",
                "bg-card border border-border shadow-sm",
                "hover:shadow-md hover:border-border hover:-translate-y-1",
                "transition-all duration-200",
                "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              )}
              title={item.description}
            >
              <div className={cn(
                "flex items-center justify-center w-12 h-12 rounded-lg mb-2 bg-muted text-foreground",
                "group-hover:bg-accent transition-colors duration-200"
              )}>
                {item.icon}
              </div>
              <span className="text-xs font-semibold text-foreground group-hover:text-foreground text-center leading-tight">
                {item.label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
